import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { FormSection, RowCard, StatusPill } from '../patterns';
import { useAuth } from '../../contexts/AuthContext';
import {
  operatorSpraySessionService,
  MyOperatorSessionRow,
  PopulatedParentSession,
} from '../../services/operatorSpraySessionService';
import type { SprayProgramRef } from '../../services/sprayService';
import { db } from '../../db';
import type { CachedOperatorCombo } from '../../db';
import { formatDateShort } from '../../dateFormat';

function statusVariant(status: string): 'ok' | 'warn' | 'danger' | 'info' {
  if (status === 'completed') return 'info';
  if (status === 'in_progress') return 'ok';
  if (status === 'cancelled') return 'danger';
  return 'warn';
}

/**
 * Pull the populated program name off the parent session. The /my route
 * server-side populates `sessionId.programId` with `{ _id, name }`; this
 * is the realistic case. Defensive fallback returns null for the raw-id
 * branch.
 */
function getProgramName(parent: PopulatedParentSession): string | null {
  const programId = parent.programId as unknown;
  if (!programId) return null;
  if (typeof programId === 'object' && 'name' in (programId as object)) {
    return (programId as SprayProgramRef).name ?? null;
  }
  return null;
}

/**
 * Unified row shape so the cache and wire-fallback paths can render through
 * the same component body. Both `MyOperatorSessionRow` (wire) and
 * `CachedOperatorCombo` (Dexie) expose the fields we surface, but the parent
 * `programId` typing differs — flatten them into a single internal record.
 *
 * Phase 6 QA fix: the row identity line now mirrors OperatorSessionList —
 * `SES-{seq} · {name}` if both are present, `SES-{seq}` if only seq is
 * present, otherwise we fall back to the program name. seq/name are sourced
 * from the parent SpraySession populated by `/operator-sessions/my`.
 */
interface DisplayRow {
  id: string;
  identityLine: string | null;
  programName: string | null;
  date: string;
  status: string;
}

function buildIdentityLine(
  seq: string | number | null | undefined,
  name: string | null | undefined,
): string | null {
  if (seq === undefined || seq === null || seq === '') return null;
  return name ? `SES-${seq} · ${name}` : `SES-${seq}`;
}

function rowFromWire(combo: MyOperatorSessionRow): DisplayRow {
  return {
    id: combo._id,
    identityLine: buildIdentityLine(combo.sessionId.seq, combo.sessionId.name),
    programName: getProgramName(combo.sessionId),
    date: combo.sessionId.date,
    status: combo.status,
  };
}

function rowFromCache(combo: CachedOperatorCombo): DisplayRow {
  return {
    id: combo._id,
    identityLine: buildIdentityLine(combo.sessionId.seq, combo.sessionId.name),
    programName: combo.sessionId.programId?.name || null,
    date: combo.sessionId.date,
    status: combo.status,
  };
}

/**
 * Operator home card — surfaces the caller's actionable combos
 * (status ∈ {not_started, in_progress}) so an operator can jump straight
 * into their work flow from the home page.
 *
 * Gating: returns `null` for non-operator callers (no linked SprayOperator
 * AND not a FarmAdmin). The fetch is skipped entirely for those callers to
 * avoid a noisy 403 against the `requiresLinkedOperator`-gated route.
 *
 * Empty-state policy: renders `null` when the fetch resolves with zero
 * combos AND the cache is empty. The card is operator-prominent; an empty
 * list on the operator's home would just be visual noise.
 *
 * Phase 6 / Task 6.5 (multi-operator spray sessions track) — initial.
 * Phase 6 / Task 6.16 — restyle to `pat-*` chrome + offline-first read.
 *   The Dexie `operatorCombos` cache (populated by the OperatorSessionList /
 *   queue drainer) is the primary source so the card renders without a
 *   network round-trip and survives offline. The service call is kept as a
 *   fallback for the first-ever-session case where the cache is empty.
 */
const MyActiveOperatorSessionsCard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [serviceCombos, setServiceCombos] = useState<MyOperatorSessionRow[]>([]);
  const [loading, setLoading] = useState(true);

  const isFarmAdmin = !!user?.role?.isFarmAdministrator;
  const hasLinkage = user?.hasLinkedOperator === true;
  const shouldFetch = !!user && (hasLinkage || isFarmAdmin);

  // Primary source: live cache. Filtered for active combos (non-terminal)
  // because the card is the operator's "what do I have to do" surface.
  // `undefined` on first render until the live-query resolves; we treat
  // `undefined` and `[]` as equivalent for the fallback decision below.
  const cachedActiveCombos = useLiveQuery(
    async () => {
      if (!shouldFetch) return [] as CachedOperatorCombo[];
      return db.operatorCombos
        .filter(c => c.status === 'not_started' || c.status === 'in_progress')
        .toArray();
    },
    [shouldFetch],
    [] as CachedOperatorCombo[],
  );

  useEffect(() => {
    if (!shouldFetch) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    operatorSpraySessionService
      .getMyOperatorSessions()
      .then(res => {
        if (!cancelled) setServiceCombos(res.sessions);
      })
      .catch(() => {
        // Best-effort card — swallow errors silently rather than blocking the
        // home page. The dedicated "My Sessions" surface in the Spray module
        // is responsible for surfacing fetch failures. The cache (if any)
        // still drives the render in this branch.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shouldFetch]);

  // Compose the display list. Cache wins; service is the fallback for the
  // "first session ever" case where the cache hasn't been seeded yet. The
  // useMemo guards against the live-query returning a fresh array reference
  // on every render.
  const rows: DisplayRow[] = useMemo(() => {
    const cacheRows = cachedActiveCombos ?? [];
    if (cacheRows.length > 0) {
      return cacheRows.map(rowFromCache);
    }
    return serviceCombos.map(rowFromWire);
  }, [cachedActiveCombos, serviceCombos]);

  // Gating: non-operator (no linkage, not FarmAdmin) → render nothing.
  if (!shouldFetch) return null;

  // Empty / still-loading: render nothing rather than an empty card.
  if (loading && rows.length === 0) return null;
  if (rows.length === 0) return null;

  return (
    <FormSection
      id="home-my-active-operator-sessions"
      title={t('home.myActiveOperatorSessions.title')}
      description={<span data-testid="my-active-operator-sessions-count">{rows.length}</span>}
    >
      <div className="d-flex flex-column gap-2">
        {rows.map(row => (
          <Link
            key={row.id}
            to={`/protection/operator-sessions/${row.id}`}
            className="text-decoration-none text-dark"
          >
            <RowCard testId={`row-card-${row.id}`}>
              <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
                <div className="d-flex justify-content-between align-items-center gap-2">
                  <span style={{ fontWeight: 600 }}>
                    {row.identityLine
                      ?? row.programName
                      ?? t('home.myActiveOperatorSessions.unnamedProgram')}
                  </span>
                  <StatusPill variant={statusVariant(row.status)}>
                    {row.status.replace('_', ' ')}
                  </StatusPill>
                </div>
                {row.identityLine && row.programName && (
                  <div style={{ fontSize: 12, color: 'var(--shell-text-muted)' }}>
                    {row.programName}
                  </div>
                )}
                <div style={{ fontSize: 12, color: 'var(--shell-text-muted)' }}>
                  {formatDateShort(row.date)}
                </div>
              </div>
            </RowCard>
          </Link>
        ))}
      </div>
      <Link
        to="/protection/my-sessions"
        className="text-decoration-none"
        style={{ fontSize: 12, color: 'var(--bs-primary, #0D6EFD)', alignSelf: 'flex-start' }}
      >
        {t('home.myActiveOperatorSessions.viewAll')}
      </Link>
    </FormSection>
  );
};

export default MyActiveOperatorSessionsCard;
export { MyActiveOperatorSessionsCard };
